import React, { useState, useEffect } from 'react';
import {
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Button,
  Row,
  Col,
  Label,
  Input,
  FormText,
} from 'reactstrap';
import { toast } from 'react-toastify';
import { proHydroApi, crudeApi } from '../../helpers/crude_api_helper';
import {
  ORIGIN_OPTIONS,
  SULFUR_CLASSIFICATION_OPTIONS,
  YIELD_CLASSIFICATION_OPTIONS,
  EMPTY_REPORT_FILTERS,
  PLANT_LOOKUP_ENDPOINT,
  COUNTRY_LOOKUP_ENDPOINT,
} from './reportConstants';

// Advance Search popup shared by the Crude reports.
//
// ⚠️ Two hosts in one popup:
//   - Plant   -> RefineryUnits/Lookup on `proHydroApi` (rha_api)
//   - Country -> Crude/GetListOriginCountries on `crudeApi` (rhacrude_api)
// Origin / Sulfur / Yield are static enums.
//
// Plant is a RefineryUnit, NOT master Fungsi (the requirement doc's wording is
// misleading) — `plantId` is a Guid and Fungsi's lookup id is a string code.
const ReportAdvanceSearchModal = ({ isOpen, filters, onSet, onCancel }) => {
  const [draft, setDraft] = useState(filters || EMPTY_REPORT_FILTERS);
  const [plantOptions, setPlantOptions] = useState([]);
  const [countryOptions, setCountryOptions] = useState([]);

  // Reset the draft to the applied filters each time the popup opens, so a
  // cancelled edit does not leak into the next open.
  useEffect(() => {
    if (isOpen) setDraft(filters || EMPTY_REPORT_FILTERS);
  }, [isOpen, filters]);

  useEffect(() => {
    fetchPlants();
    fetchCountries();
  }, []);

  const fetchPlants = async () => {
    try {
      // Tenant-scoped server-side — no client filtering needed.
      const result = await proHydroApi.get(PLANT_LOOKUP_ENDPOINT);
      setPlantOptions(Array.isArray(result?.items) ? result.items : []);
    } catch (error) {
      toast.error(`Failed to load Plant: ${error}`);
      setPlantOptions([]);
    }
  };

  const fetchCountries = async () => {
    try {
      // Distinct Origin+Country pairs actually present in the Crude data.
      const result = await crudeApi.get(COUNTRY_LOOKUP_ENDPOINT);
      setCountryOptions(Array.isArray(result?.items) ? result.items : []);
    } catch (error) {
      toast.error(`Failed to load Country: ${error}`);
      setCountryOptions([]);
    }
  };

  const handleChange = e => {
    const { name, value } = e.target;
    if (name === 'origin') {
      // Changing Origin narrows the Country list. Any country already picked
      // that does not belong to the new Origin must be dropped too — otherwise
      // it stays in the filter while being invisible in the (now filtered)
      // select, and the grid comes back empty with nothing on screen to
      // explain why.
      setDraft(prev => ({
        ...prev,
        origin: value,
        country: value
          ? prev.country.filter(name_ =>
              countryOptions.some(
                c => c.country === name_ && c.origin === value
              )
            )
          : prev.country,
      }));
      return;
    }
    setDraft(prev => ({ ...prev, [name]: value }));
  };

  const handleCountryChange = e => {
    const selected = Array.from(e.target.selectedOptions, o => o.value);
    setDraft(prev => ({ ...prev, country: selected }));
  };

  const handleReset = () => setDraft(EMPTY_REPORT_FILTERS);

  // The lookup carries `origin` per country, so narrow the list when an Origin
  // is picked. The backend treats the two filters independently; this is purely
  // to stop the user selecting a country that cannot match.
  const visibleCountries = draft.origin
    ? countryOptions.filter(c => c.origin === draft.origin)
    : countryOptions;

  // Distinct country names (the same country can appear under both origins).
  const countryNames = [...new Set(visibleCountries.map(c => c.country))];

  return (
    <Modal isOpen={isOpen} toggle={onCancel} centered size='lg'>
      <ModalHeader toggle={onCancel}>Advance Search</ModalHeader>
      <ModalBody>
        <Row>
          <Col md={6}>
            <div className='mb-3'>
              <Label for='plantId' className='form-label'>
                Plant
              </Label>
              <Input
                type='select'
                id='plantId'
                name='plantId'
                value={draft.plantId}
                onChange={handleChange}
              >
                <option value=''>All Plant</option>
                {plantOptions.map(p => (
                  <option key={p.id} value={p.id}>
                    {p.text}
                  </option>
                ))}
              </Input>
            </div>
          </Col>
          <Col md={6}>
            <div className='mb-3'>
              <Label for='origin' className='form-label'>
                Origin
              </Label>
              <Input
                type='select'
                id='origin'
                name='origin'
                value={draft.origin}
                onChange={handleChange}
              >
                <option value=''>All Origin</option>
                {ORIGIN_OPTIONS.map(o => (
                  <option key={o.value} value={o.value}>
                    {o.label}
                  </option>
                ))}
              </Input>
            </div>
          </Col>
          <Col md={6}>
            <div className='mb-3'>
              <Label for='country' className='form-label'>
                Country
              </Label>
              <Input
                type='select'
                id='country'
                name='country'
                multiple
                value={draft.country}
                onChange={handleCountryChange}
                style={{ minHeight: '120px' }}
              >
                {countryNames.map(c => (
                  <option key={c} value={c}>
                    {c}
                  </option>
                ))}
              </Input>
              <FormText>Hold Ctrl/Cmd to select more than one.</FormText>
            </div>
          </Col>
          <Col md={6}>
            <div className='mb-3'>
              <Label for='sulfurClassification' className='form-label'>
                Sulfur Clasification
              </Label>
              <Input
                type='select'
                id='sulfurClassification'
                name='sulfurClassification'
                value={draft.sulfurClassification}
                onChange={handleChange}
              >
                <option value=''>All Sulfur Clasification</option>
                {SULFUR_CLASSIFICATION_OPTIONS.map(s => (
                  <option key={s.value} value={s.value}>
                    {s.label}
                  </option>
                ))}
              </Input>
            </div>
            <div className='mb-3'>
              <Label for='yieldClassification' className='form-label'>
                Yield Clasification
              </Label>
              <Input
                type='select'
                id='yieldClassification'
                name='yieldClassification'
                value={draft.yieldClassification}
                onChange={handleChange}
              >
                <option value=''>All Yield Clasification</option>
                {YIELD_CLASSIFICATION_OPTIONS.map(y => (
                  <option key={y.value} value={y.value}>
                    {y.label}
                  </option>
                ))}
              </Input>
            </div>
          </Col>
          <Col md={12}>
            <div className='mb-3'>
              <Label for='searchText' className='form-label'>
                Search
              </Label>
              <Input
                type='text'
                id='searchText'
                name='searchText'
                placeholder='Keyword...'
                value={draft.searchText}
                onChange={handleChange}
              />
            </div>
          </Col>
        </Row>
      </ModalBody>
      <ModalFooter>
        <Button
          color='light'
          className='btn-sm me-auto'
          onClick={handleReset}
          data-testid='advance-search-reset'
        >
          Reset
        </Button>
        <Button
          color='light'
          className='btn-sm'
          onClick={onCancel}
          data-testid='advance-search-cancel'
        >
          Cancel
        </Button>
        <Button
          color='primary'
          className='btn-sm'
          onClick={() => onSet(draft)}
          data-testid='advance-search-set'
        >
          Set
        </Button>
      </ModalFooter>
    </Modal>
  );
};

export default ReportAdvanceSearchModal;
